import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import { MediaKitAnalyticsLayout } from "./MediaKitAnalyticsLayout";
import { MediaKitDemographyTextBlock } from "./MediaKitDemographyTextBlock";
import type { PodcastMediaKit as PodcastMediaKitType } from "../../api/podcasts";
import {
  MediaKitYoutubeCatalogConsumptionGrid,
  MediaKitConsumptionSubsection,
  catalogEngagementPercentFromConsumption,
} from "./MediaKitCatalogConsumptionCards";
import { MediaKitYoutubeCatalogDrawer } from "./MediaKitYoutubeCatalogDrawer";
import { useTranslation } from "react-i18next";

interface MediaKitYouTubeAnalyticsSectionProps {
  analytics: PodcastMediaKitType["analytics"];
  platformUrl?: string | null;
  longFormContentCount?: number;
  shortsContentCount?: number;
  podcastSlug?: string;
  companySlug?: string;
  selectedMonths?: string[];
  programSlug?: string;
}

export const MediaKitYouTubeAnalyticsSection: React.FC<
  MediaKitYouTubeAnalyticsSectionProps
> = ({
  analytics,
  platformUrl: _platformUrl,
  longFormContentCount,
  shortsContentCount,
  podcastSlug,
  companySlug,
  selectedMonths,
  programSlug,
}) => {
  const { t } = useTranslation("mediaKit");
  const yt = analytics?.youtube;
  const longFormConsumption = yt?.longForm?.consumption ?? yt?.consumption;
  const longFormEngagement = yt?.longForm?.engagement ?? yt?.engagement;
  const longFormAudience = yt?.longForm?.audience ?? yt?.audience;
  const shortsConsumption = yt?.shorts?.consumption;
  const shortsAudienceOnly = yt?.shorts?.audience;
  const [catalogDrawerFormat, setCatalogDrawerFormat] = useState<
    "longForm" | "shorts" | null
  >(null);

  const oauthEngagementRate = longFormEngagement?.engagementRate;
  const hasOAuthEngagementRate = oauthEngagementRate != null;
  /** Match Shorts: rate from the same catalog totals as the consumption cards (OAuth demographics often report 0 while catalog has real interactions). */
  const longFormCatalogEngagement =
    catalogEngagementPercentFromConsumption(longFormConsumption);
  const longFormDisplayEngagementRate =
    longFormCatalogEngagement != null
      ? longFormCatalogEngagement
      : hasOAuthEngagementRate
        ? oauthEngagementRate
        : undefined;

  if (!yt) {
    return null;
  }

  const hasLongFormConsumption =
    longFormConsumption != null &&
    Object.values(longFormConsumption).some((v) => v != null);
  const hasLongFormAudienceData = Boolean(
    longFormAudience &&
    (longFormAudience.gender?.length || longFormAudience.ageRanges?.length),
  );
  const hasShortsAudienceData = Boolean(
    shortsAudienceOnly &&
    (shortsAudienceOnly.gender?.length || shortsAudienceOnly.ageRanges?.length),
  );
  /** Single audiencia block for YouTube: long-form first, else Shorts-only OAuth. */
  const youtubeAudience =
    hasLongFormAudienceData && longFormAudience
      ? longFormAudience
      : hasShortsAudienceData && shortsAudienceOnly
        ? shortsAudienceOnly
        : undefined;
  const hasYoutubeAudience = Boolean(youtubeAudience);
  const hasShortsConsumptionMetrics =
    shortsConsumption != null &&
    Object.values(shortsConsumption).some((v) => v != null);
  const shortsCatalogEngagement =
    catalogEngagementPercentFromConsumption(shortsConsumption);

  const showYouTubeVideosCatalog =
    hasLongFormConsumption ||
    hasOAuthEngagementRate ||
    longFormDisplayEngagementRate != null;
  const showAudienceOnlyVideos =
    hasYoutubeAudience &&
    !showYouTubeVideosCatalog &&
    !hasShortsConsumptionMetrics;
  const showOAuthAfterCatalog =
    hasYoutubeAudience &&
    (hasLongFormConsumption ||
      hasOAuthEngagementRate ||
      longFormDisplayEngagementRate != null ||
      hasShortsConsumptionMetrics);

  if (
    !hasLongFormConsumption &&
    !hasOAuthEngagementRate &&
    longFormDisplayEngagementRate == null &&
    !hasYoutubeAudience &&
    !hasShortsConsumptionMetrics
  ) {
    return null;
  }

  const audienceBlock =
    hasYoutubeAudience && youtubeAudience ? (
      <MediaKitDemographyTextBlock
        gender={youtubeAudience.gender}
        ageRanges={youtubeAudience.ageRanges}
      />
    ) : null;

  const detailsButton = (format: "longForm" | "shorts") =>
    podcastSlug != null && podcastSlug !== "" ? (
      <Button
        variant="text"
        data-mediakit-pdf-exclude=""
        onClick={() => setCatalogDrawerFormat(format)}
        sx={{
          textTransform: "none",
          textDecoration: "underline",
          fontWeight: 500,
          fontSize: "0.8125rem",
          color: "text.secondary",
          minWidth: 0,
          px: 0,
          py: 0,
          "&:hover": {
            textDecoration: "underline",
            background: "none",
            color: "text.primary",
          },
        }}
      >
        {t("seeDetails")}
      </Button>
    ) : null;

  return (
    <MediaKitAnalyticsLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        {(showYouTubeVideosCatalog || longFormContentCount != null) && (
          <MediaKitConsumptionSubsection
            title="YouTube Videos"
            headerAction={detailsButton("longForm")}
            metricsWindow={{
              format: "longForm",
              inProgressCount: yt?.longForm?.consumption?.inProgressCount,
            }}
          >
            <MediaKitYoutubeCatalogConsumptionGrid
              consumption={longFormConsumption}
              engagementRate={longFormDisplayEngagementRate}
              contentCount={longFormContentCount}
            />
          </MediaKitConsumptionSubsection>
        )}
        {showAudienceOnlyVideos && audienceBlock && !longFormContentCount && (
          <MediaKitConsumptionSubsection title="YouTube Videos">
            {audienceBlock}
          </MediaKitConsumptionSubsection>
        )}
        {hasShortsConsumptionMetrics && (
          <MediaKitConsumptionSubsection
            title="YouTube Shorts"
            metricsWindow={{
              format: "shorts",
              inProgressCount: shortsConsumption?.inProgressCount,
            }}
          >
            <MediaKitYoutubeCatalogConsumptionGrid
              consumption={shortsConsumption}
              engagementRate={
                shortsCatalogEngagement != null
                  ? shortsCatalogEngagement
                  : undefined
              }
              contentCount={shortsContentCount}
              contentCountLabel="Shorts"
              contentCountIcon="short"
            />
          </MediaKitConsumptionSubsection>
        )}
        {showOAuthAfterCatalog && audienceBlock && (
          <Box component="section" data-mediakit-pdf-section="" sx={{ pb: 4 }}>
            {audienceBlock}
          </Box>
        )}
      </Box>
      {podcastSlug ? (
        <MediaKitYoutubeCatalogDrawer
          open={catalogDrawerFormat != null}
          onClose={() => setCatalogDrawerFormat(null)}
          podcastSlug={podcastSlug}
          companySlug={companySlug}
          selectedMonths={selectedMonths}
          programSlug={programSlug}
          format={catalogDrawerFormat ?? "longForm"}
        />
      ) : null}
    </MediaKitAnalyticsLayout>
  );
};
